import axios from 'axios';
import jwt_decode from "jwt-decode";
import { useMaterialUIController, setToken, setLoggedIn, setRefreshToken } from "../../context";
import toast from "react-hot-toast";

// Treat a token as expired this many seconds early, to absorb client/server
// clock skew and avoid sending a request that lands just after expiry.
const TOKEN_EXPIRY_SKEW_SECONDS = 30;

const isTokenExpired = (jwt) => {
    if (!jwt || jwt === "undefined") return true;
    try {
        const { exp } = jwt_decode(jwt);
        if (!exp) return false; // no exp claim → let the server decide
        return Date.now() >= (exp - TOKEN_EXPIRY_SKEW_SECONDS) * 1000;
    } catch (_) {
        return true; // unparseable → force refresh/logout path
    }
};

const baseUrl = process.env.REACT_APP_BASE_URL;
const api = axios.create({
    baseURL: baseUrl + '/api/adminPortal/v1/', // Replace this with your API's base URL
});

// Full URL of the refresh endpoint — called with a bare axios client so it
// never passes through this instance's interceptors (no recursion).
const REFRESH_URL = baseUrl + '/api/adminPortal/v1/auth/admin/refresh';

// List of URLs that should not include the authorization header
const excludeAuthorizationUrls = [
    'auth/admin/authenticate',
    'auth/admin/forgot-password',
    'auth/admin/reset-password',
    'auth/admin/refresh',
];

// Module-level interceptor IDs — eject before re-adding to prevent accumulation
let requestInterceptorId = null;
let responseInterceptorId = null;

// Prevent duplicate toast + redirect when multiple in-flight requests all 401
let sessionExpiredHandled = false;

// Single-flight refresh: concurrent 401s share one refresh call
let refreshPromise = null;
// Latest dispatch captured from the most recent useApi() render.
let latestDispatch = null;

const readStoredState = () => {
    try {
        return JSON.parse(localStorage.getItem("materialUIState") || "{}");
    } catch (_) {
        return {};
    }
};

const clearStoredAuth = () => {
    try {
        const stored = readStoredState();
        stored.token = "";
        stored.refreshToken = "";
        stored.isLoggedIn = false;
        localStorage.setItem("materialUIState", JSON.stringify(stored));
    } catch (_) { /* ignore */ }
};

// Returns a new access token, or throws if refresh isn't possible.
const refreshAccessToken = () => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            const stored = readStoredState();
            const refreshToken = stored.refreshToken;
            if (!refreshToken || refreshToken === "undefined") {
                throw new Error("No refresh token available");
            }
            const res = await axios.post(REFRESH_URL, { refreshToken });
            const newToken = res?.data?.token;
            if (!newToken) {
                throw new Error("Refresh response had no token");
            }
            stored.token = newToken;
            localStorage.setItem("materialUIState", JSON.stringify(stored));
            api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            if (latestDispatch) setToken(latestDispatch, newToken);
            return newToken;
        })().finally(() => { refreshPromise = null; });
    }
    return refreshPromise;
};

const useApi = () => {
    const [controller, dispatch] = useMaterialUIController();
    const { token } = controller;
    latestDispatch = dispatch;

    const setAuthToken = () => {
        if (token && token !== 'undefined') {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete api.defaults.headers.common['Authorization'];
        }
    };

    setAuthToken();

    const handleSessionExpired = () => {
        if (sessionExpiredHandled) return;
        sessionExpiredHandled = true;

        toast.error("Session expired. Please log in again.");

        clearStoredAuth();

        setToken(dispatch, "");
        setRefreshToken(dispatch, "");
        setLoggedIn(dispatch, false);

        window.location.replace("/login");
    };

    // Eject previous interceptors so only one of each exists at a time
    if (requestInterceptorId !== null) {
        api.interceptors.request.eject(requestInterceptorId);
    }
    if (responseInterceptorId !== null) {
        api.interceptors.response.eject(responseInterceptorId);
    }

    requestInterceptorId = api.interceptors.request.use(async (config) => {
        document.body.classList.add('loading-indicator');

        // Check if the URL should exclude the authorization header
        if (excludeAuthorizationUrls.some(url => config.url.includes(url))) {
            delete config.headers['Authorization'];
            return config;
        }

        // Proactive: if the access token is already expired, refresh before sending
        // so we don't waste a guaranteed-401 round trip. The reactive 401 handler
        // below still covers server-side invalidation / clock skew.
        const stored = readStoredState();
        if (isTokenExpired(stored.token) && stored.refreshToken && stored.refreshToken !== "undefined") {
            try {
                const newToken = await refreshAccessToken();
                config.headers['Authorization'] = `Bearer ${newToken}`;
            } catch (_) {
                // Refresh token gone/expired — let the request go out and the
                // 401 handler perform the logout, keeping one logout path.
            }
        }

        return config;
    }, (error) => {
        document.body.classList.remove('loading-indicator');
        return Promise.reject(error);
    });

    responseInterceptorId = api.interceptors.response.use((response) => {
        if (response.status === 406 && response.data.error === "license_expired") {
            document.body.classList.add('license-indicator');
        }
        document.body.classList.remove('loading-indicator');
        return response;
    }, async (error) => {
        document.body.classList.remove('loading-indicator');

        const original = error.config;
        const status = error.response?.status;

        // Access token expired/invalid: try a one-time refresh, then replay.
        const isAuthUrl = original && excludeAuthorizationUrls.some(url => original.url?.includes(url));
        if (status === 401 && original && !original._retry && !isAuthUrl) {
            original._retry = true;
            try {
                const newToken = await refreshAccessToken();
                original.headers = original.headers || {};
                original.headers['Authorization'] = `Bearer ${newToken}`;
                return api(original);
            } catch (refreshErr) {
                // Refresh token missing/expired/invalid → log out.
                handleSessionExpired();
                return Promise.reject(error);
            }
        }

        if (status === 406 && error.response?.data?.error === "license_expired") {
            document.body.classList.add('license-indicator');
        }
        return Promise.reject(error);
    });

    return api;
};

export default useApi;
